import {
  breakDepth,
  creatureX,
  crestDepth,
  depthAt,
  foamSpeed,
  reformDepth,
  swellSpeed,
  TREASURE_POINTS,
  type Course,
  type Rip,
  type Tier,
} from './course';
import { TUNING } from './tuning';

/**
 * 1 フレーム分の入力。これだけがシミュレーションへの外部入力であり、
 * シード + 入力列を保存すれば走行が完全に再現できる。
 */
export interface Input {
  /** -1（左）〜 +1（右）。 */
  readonly lateral: number;
  /** そのフレームでダイブが押されたか（押しっぱなしではなく、押した瞬間だけ true）。 */
  readonly dive: boolean;
}

export const NO_INPUT: Input = { lateral: 0, dive: false };

/** 走行中の波。course の SwellSeed から生成され、位置を時間積分される。 */
export interface Swell {
  readonly id: number;
  readonly amp: number;
  /** 浜からの距離（m）。時間とともに減っていく。 */
  y: number;
  /** 崩れて白波になっているか。リーフを越えて深いところへ戻ると形を取り戻す。 */
  broken: boolean;
  /** 掘れ上がっていて、もうすぐ崩れる。描画で予告し、乗っていればいちばん速い。 */
  cresting: boolean;
  /**
   * この波とプレイヤーの判定が済んだか。
   * 崩れの先頭が到達した瞬間に 1 回だけ判定し、くぐり抜けたらこの波はもう当たらない
   * （実際のドルフィンスルーと同じで、リップをやり過ごせば後ろの泡は抜けられる）。
   * 形を取り戻すと false に戻る。
   */
  settled: boolean;
}

export type SimEvent =
  | { readonly kind: 'dive' }
  | { readonly kind: 'perfect'; readonly combo: number }
  | { readonly kind: 'ride'; readonly combo: number }
  /** 乗っていた波が目の前で崩れた。猶予のあいだに潜れば助かる。 */
  | { readonly kind: 'closeout' }
  | { readonly kind: 'caught'; readonly lostPoints: number }
  | { readonly kind: 'pickup'; readonly tier: Tier; readonly points: number }
  /** クラゲに刺された。押し戻されないが、しばらく操作できない。 */
  | { readonly kind: 'sting' }
  /** イルカに掴まった。 */
  | { readonly kind: 'boost' }
  /** 子ガメを拾った。浜まで運べば加点。 */
  | { readonly kind: 'hatchling'; readonly carried: number }
  | { readonly kind: 'finish' };

export interface SimState {
  /** 経過時間（秒）。固定ステップの積算なので端末に依存しない。 */
  t: number;
  frame: number;

  /** 横位置（m, 0..laneWidth）。 */
  x: number;
  /** 浜からの距離（m）。0 に到達すると帰還。 */
  y: number;
  /** 実際の岸方向の速度（m/s）。演出用に保持する。 */
  speed: number;

  breath: number;
  /** 0 より大きい間は潜行中。 */
  diveTimer: number;
  /** 直近のダイブを開始してからの経過時間。パーフェクト判定に使う。 */
  diveElapsed: number;
  /** 0 より大きい間は巻かれて操作不能。 */
  stunTimer: number;
  /** クラゲ。0 より大きい間は操作できないが、押し戻されはしない。 */
  numbTimer: number;
  /** イルカ。0 より大きい間は加速している。 */
  boostTimer: number;
  /**
   * 0 より大きい間は白波に巻かれない。
   * 目の前で波が崩れたときの反応猶予と、被弾直後の連鎖防止を兼ねる。
   */
  graceTimer: number;
  /** 斜面に乗り続けている時間。 */
  rideTimer: number;
  riding: boolean;
  /** 乗っている波が掘れ上がっている。いちばん速いが、もうすぐ崩れる。 */
  ridingCresting: boolean;
  /** 崩れの先頭（当たり判定のある帯）の中にいる。描画と演出用。 */
  inImpact: boolean;
  inRip: boolean;

  combo: number;
  bestCombo: number;
  points: number;

  swells: Swell[];
  /** 未リリースの波を指すカーソル。releaseTime 昇順を前提にする。 */
  nextSwell: number;
  /** 取得済みの宝 id を取得順に保持する。巻かれたときは後ろから落とす。 */
  carried: number[];
  collected: Set<number>;
  /** 触れ終わった生き物。二度は反応しない。 */
  touchedCreatures: Set<number>;
  /** 連れている子ガメの数。浜まで運べば加点になる。 */
  hatchlings: number;

  finished: boolean;
  /** そのフレームで起きたこと。描画と効果音がここを読む。シミュレーションには影響しない。 */
  events: SimEvent[];
}

export function createSim(course: Course): SimState {
  return {
    t: 0,
    frame: 0,
    x: course.stage.laneWidth / 2,
    y: course.stage.length,
    speed: 0,
    breath: TUNING.breathMax,
    diveTimer: 0,
    diveElapsed: 0,
    stunTimer: 0,
    numbTimer: 0,
    boostTimer: 0,
    graceTimer: 0,
    rideTimer: 0,
    riding: false,
    ridingCresting: false,
    inImpact: false,
    inRip: false,
    combo: 0,
    bestCombo: 0,
    points: 0,
    swells: [],
    nextSwell: 0,
    carried: [],
    collected: new Set<number>(),
    touchedCreatures: new Set<number>(),
    hatchlings: 0,
    finished: false,
    events: [],
  };
}

function insideRip(rip: Rip, x: number, y: number): boolean {
  return y >= rip.yNear && y <= rip.yFar && Math.abs(x - rip.xCenter) <= rip.halfWidth;
}

/**
 * 1 フレーム進める。固定タイムステップ専用で、可変 dt は受け取らない。
 * 浮動小数の演算順序を変えると過去のリプレイが再生できなくなるため、
 * この関数の計算順序は互換性の対象として扱う。
 */
export function step(state: SimState, course: Course, input: Input): void {
  if (state.finished) return;

  const dt = TUNING.dt;
  const stage = course.stage;
  state.events.length = 0;
  state.t += dt;
  state.frame += 1;

  // ── 1. 波の投入 ──────────────────────────────
  const seeds = course.swells;
  while (state.nextSwell < seeds.length && seeds[state.nextSwell].releaseTime <= state.t) {
    const seed = seeds[state.nextSwell];
    state.swells.push({
      id: seed.id,
      amp: seed.amp,
      y: stage.length + 12,
      broken: false,
      cresting: false,
      settled: false,
    });
    state.nextSwell += 1;
  }

  // ── 2. 波の進行、砕波、崩れ戻り ───────────────
  state.graceTimer = Math.max(0, state.graceTimer - dt);
  for (let i = state.swells.length - 1; i >= 0; i--) {
    const swell = state.swells[i];
    // 崩れた波（段波）は元のうねりより速く走る。下限を掛けないと浜の近くで
    // 泳者と同速になり、衝撃帯が居座って 1 回のダイブでは抜けられなくなる。
    swell.y -=
      (swell.broken
        ? foamSpeed(stage, swell.y, state.t)
        : swellSpeed(stage, swell.y, state.t)) * dt;

    const depth = depthAt(stage, swell.y, state.t);
    if (!swell.broken) {
      swell.cresting = depth < crestDepth(swell.amp);
      if (depth < breakDepth(swell.amp)) {
        swell.broken = true;
        swell.cresting = false;
        // 目の前で崩れた場合は反応の猶予を与える。乗っていた位置がそのまま
        // 当たり判定の内側になって即被弾する、という理不尽をここで防ぐ。
        const gap = swell.y - state.y;
        if (gap > -TUNING.foamHitHalf && gap <= TUNING.faceWindow) {
          state.graceTimer = Math.max(state.graceTimer, TUNING.breakGrace);
          state.events.push({ kind: 'closeout' });
        }
      }
    } else if (depth > reformDepth(swell.amp)) {
      // リーフを越えてラグーンに入ると形を取り戻す。加点もやり直せるようにする。
      swell.broken = false;
      swell.cresting = false;
      swell.settled = false;
    }
    if (swell.y < -8) state.swells.splice(i, 1);
  }

  // ── 3. 巻かれている間は操作不能 ───────────────
  if (state.stunTimer > 0) {
    state.stunTimer -= dt;
    state.y += TUNING.caughtPushback * dt * Math.min(1, state.stunTimer / TUNING.caughtStun + 0.15);
    state.y = Math.min(state.y, stage.length + 20);
    state.speed = -TUNING.caughtPushback;
    state.riding = false;
    state.inImpact = false;
    state.rideTimer = 0;
    state.breath = Math.min(TUNING.breathMax, state.breath + TUNING.breathRefill * dt);
    return;
  }

  // ── 3b. クラゲに刺されている間 ────────────────
  // 巻かれるのと違い、押し戻されない。前へは流されるが舵もダイブも効かない。
  state.numbTimer = Math.max(0, state.numbTimer - dt);
  const numb = state.numbTimer > 0;

  // ── 4. ダイブの発動と持続 ────────────────────
  if (!numb && input.dive && state.diveTimer <= 0 && state.breath >= TUNING.diveCost) {
    state.diveTimer = TUNING.diveTime;
    state.diveElapsed = 0;
    state.breath -= TUNING.diveCost;
    state.events.push({ kind: 'dive' });
  }
  const diving = state.diveTimer > 0;
  if (diving) {
    state.diveTimer -= dt;
    state.diveElapsed += dt;
  } else {
    state.breath = Math.min(TUNING.breathMax, state.breath + TUNING.breathRefill * dt);
  }

  // ── 5. 横移動 ────────────────────────────────
  const lateral = numb ? 0 : Math.max(-1, Math.min(1, input.lateral));
  const lateralFactor = diving ? TUNING.diveLateralFactor : 1;
  state.x += lateral * TUNING.lateralSpeed * lateralFactor * dt;
  state.x = Math.max(0.5, Math.min(stage.laneWidth - 0.5, state.x));

  // ── 6. 岸方向の速度 ──────────────────────────
  const carryDrag = Math.min(TUNING.carryDragMax, state.carried.length * TUNING.carryDrag);
  let forward =
    TUNING.swimSpeed *
    (1 - TUNING.lateralCost * Math.abs(lateral)) *
    (diving ? TUNING.diveForwardFactor : 1) *
    (1 - carryDrag);

  // 崩れていない波の斜面に乗っていれば加速する。潜っていると乗れない。
  state.riding = false;
  state.ridingCresting = false;
  if (!diving) {
    for (const swell of state.swells) {
      if (swell.broken) continue;
      const gap = swell.y - state.y; // 正なら波の峰は自分より沖側（つまり自分は前面の斜面にいる）
      if (gap > 0 && gap <= TUNING.faceWindow) {
        state.riding = true;
        if (swell.cresting) state.ridingCresting = true;
        break;
      }
    }
  }
  // イルカに掴まっている間は、波乗りより速い。寄り道してでも掴まえに行く価値を作る。
  state.boostTimer = Math.max(0, state.boostTimer - dt);
  if (state.boostTimer > 0) {
    forward = Math.max(forward, TUNING.dolphinSpeed * (1 - TUNING.lateralCost * Math.abs(lateral)));
  }

  if (state.riding) {
    // 波に乗っていても横に振れば遅くなる。ここで横移動のコストを免除すると、
    // 乗っている間だけ寄り道が無料になり、タイムと宝が対立しなくなる。
    // 掘れ上がっている波はいちばん速い。崩れる直前が最高の斜面であり、
    // 同時にいちばん危ないという読み合いになる。
    const ridingBoost = state.ridingCresting ? TUNING.crestSurfBonus : 1;
    forward = Math.max(
      forward,
      TUNING.surfSpeed * ridingBoost * (1 - TUNING.lateralCost * Math.abs(lateral)),
    );
    state.rideTimer += dt;
    if (state.rideTimer >= TUNING.rideCombo) {
      state.combo += 1;
      state.bestCombo = Math.max(state.bestCombo, state.combo);
      state.rideTimer = 0;
      state.events.push({ kind: 'ride', combo: state.combo });
    }
  } else {
    state.rideTimer = 0;
  }

  // ── 7. 崩れの先頭との接触 ────────────────────
  //
  // 危険なのは崩れの先頭だけで、その後ろに残る泡は泳いで抜けられる。
  // 判定は「先頭が到達した瞬間」の 1 回きり。くぐり抜けたらその波はもう当たらない。
  //
  // ここを「帯に重なっているあいだ継続で判定する」形にすると、守りが帯の途中で
  // 切れて被弾する。しかもそのときには泡が視覚的に自分を追い越しているので、
  // プレイヤーには何が起きたのか分からない（実測でこの形の被弾ばかりになった）。
  for (const swell of state.swells) {
    if (!swell.broken || swell.settled) continue;
    if (Math.abs(swell.y - state.y) > TUNING.foamHitHalf) continue;

    // この波の判定はこれで決着。以降この波では巻かれない。
    swell.settled = true;

    if (diving) {
      // 潜っていれば必ず助かる。精度はスコアにだけ効く。
      if (state.diveElapsed <= TUNING.dolphinWindow) {
        state.combo += 1;
        state.bestCombo = Math.max(state.bestCombo, state.combo);
        state.breath = Math.min(
          TUNING.breathMax,
          state.breath + TUNING.dolphinBreathRefund,
        );
        forward += TUNING.dolphinBoost;
        state.events.push({ kind: 'perfect', combo: state.combo });
      }
      continue;
    }

    // 目の前で崩れた直後の猶予中、または被弾直後の無敵中は巻かれない。
    if (state.graceTimer > 0) continue;

    // 巻かれた。宝を落とし、コンボが切れ、沖へ押し戻される。
    let lostPoints = 0;
    for (let i = 0; i < TUNING.caughtDropCount && state.carried.length > 0; i++) {
      const droppedId = state.carried.pop();
      if (droppedId === undefined) break;
      const treasure = course.treasures.find((tr) => tr.id === droppedId);
      if (treasure) lostPoints += TREASURE_POINTS[treasure.tier];
      state.collected.delete(droppedId);
    }
    state.points = Math.max(0, state.points - lostPoints);
    state.combo = 0;
    state.stunTimer = TUNING.caughtStun;
    // 立ち直った直後に次の泡へ押し込まれて連鎖被弾するのを防ぐ。
    state.graceTimer = TUNING.caughtStun + TUNING.postCatchGrace;
    state.events.push({ kind: 'caught', lostPoints });
    state.speed = -TUNING.caughtPushback;
    return;
  }

  // 演出用。当たり判定とは別に、見た目の泡に重なっているかを持つ。
  state.inImpact = state.swells.some(
    (swell) => swell.broken && Math.abs(swell.y - state.y) <= TUNING.foamVisualHalf,
  );

  // ── 8. 離岸流 ────────────────────────────────
  state.inRip = false;
  for (const rip of course.rips) {
    if (insideRip(rip, state.x, state.y)) {
      state.inRip = true;
      state.y += TUNING.ripPush * dt;
      break;
    }
  }

  // ── 9. 前進 ──────────────────────────────────
  state.y -= forward * dt;
  state.speed = forward - (state.inRip ? TUNING.ripPush : 0);

  // ── 10. 宝の取得 ─────────────────────────────
  for (const treasure of course.treasures) {
    if (state.collected.has(treasure.id)) continue;
    if (Math.abs(treasure.y - state.y) > TUNING.pickupRadius) continue;
    if (Math.abs(treasure.x - state.x) > TUNING.pickupRadius) continue;
    state.collected.add(treasure.id);
    state.carried.push(treasure.id);
    const points = TREASURE_POINTS[treasure.tier];
    state.points += points;
    state.events.push({ kind: 'pickup', tier: treasure.tier, points });
  }

  // ── 10b. 生き物との接触 ──────────────────────
  // クラゲは避けるもの、イルカと子ガメは頼るもの。
  // 「避けるだけ」にすると海が敵だらけになり、泳いでいる感触が出ない。
  for (const creature of course.creatures) {
    if (state.touchedCreatures.has(creature.id)) continue;
    if (Math.abs(creature.y - state.y) > TUNING.creatureRadius) continue;
    const cx = creatureX(creature, state.t);
    if (Math.abs(cx - state.x) > TUNING.creatureRadius) continue;

    switch (creature.kind) {
      case 'jellyfish':
        // 潜っていれば下をやり過ごせる。ダイブが波以外にも効く。
        if (diving) continue;
        state.touchedCreatures.add(creature.id);
        state.numbTimer = TUNING.stingTime;
        state.combo = 0;
        state.events.push({ kind: 'sting' });
        break;
      case 'dolphin':
        state.touchedCreatures.add(creature.id);
        state.boostTimer = TUNING.dolphinBoostTime;
        state.events.push({ kind: 'boost' });
        break;
      case 'hatchling':
        state.touchedCreatures.add(creature.id);
        state.hatchlings += 1;
        state.events.push({ kind: 'hatchling', carried: state.hatchlings });
        break;
    }
  }

  // ── 11. 帰還判定 ─────────────────────────────
  if (state.y <= 0) {
    state.y = 0;
    state.finished = true;
    state.events.push({ kind: 'finish' });
  }
}

/** 走行の進捗（0 = 沖、1 = 浜）。距離バーに使う。 */
export function progress(state: SimState, course: Course): number {
  return Math.max(0, Math.min(1, 1 - state.y / course.stage.length));
}
